/**
 * T018/T019 — 排班管理（模块十四）
 *
 * T018: 节假日日历维护 + 排班 xlsx 导入（干运行预览 + 确认落库）
 * T019: 排班网格视图（手动逐格编辑 / Shift 批量）+ 月度统计（D3 九列）
 *       成员只读视图在 PersonalTasksPage 日历集成
 */

import { useEffect, useRef, useState, useCallback, CSSProperties } from 'react'
import { useAuth } from '../auth'
import { useLang } from '../i18n'
import { usePrefs, type ThemeMode } from '../prefs'

// ── Types ───────────────────────────────────────────────────────────────────

interface HolidayEntry {
  id: string
  date: string
  region_code: string
  type: string
  name: string | null
}

interface ParsedRow {
  member_id: string
  member_name: string
  date: string
  raw_status: string
  is_working: boolean
  is_override: boolean
}

interface ParseWarning {
  row_ref: string
  message: string
}

interface DryRunResult {
  batch_id: string
  members: number
  days: number
  added: number
  overridden: number
  warnings: ParseWarning[]
  rows: ParsedRow[]
}

interface ScheduleCell {
  id: string
  member_id: string
  member_name: string | null
  date: string
  raw_status: string
  is_working: boolean
  source: string
}

interface MemberInfo {
  id: string
  display_name: string
  group_name: string
  region_code: string
}

interface MonthlyStatRow {
  member_id: string
  member_name: string
  on_workday: number
  on_nonworkday: number
  off_workday: number
  off_nonworkday: number
  vacation: number
  leave: number
  overtime: number
  overtime_comp_off: number
  total_on_duty: number
}

// ── Constants ────────────────────────────────────────────────────────────────

const ALL_STATUSES = [
  'on_workday',
  'on_nonworkday',
  'off_workday',
  'off_nonworkday',
  'vacation',
  'leave_morning',
  'leave_afternoon',
  'leave_full_day',
  'overtime',
  'overtime_morning',
  'overtime_afternoon',
  'overtime_full_day',
  'overtime_comp_off',
] as const

type RawStatus = typeof ALL_STATUSES[number]

// Light mode status colors
const STATUS_COLOR: Record<string, string> = {
  on_workday: '#4caf50',
  on_nonworkday: '#8bc34a',
  off_workday: '#ff9800',
  off_nonworkday: '#9e9e9e',
  vacation: '#2196f3',
  leave_morning: '#f06292',
  leave_afternoon: '#f06292',
  leave_full_day: '#e91e63',
  overtime: '#9c27b0',
  overtime_morning: '#ce93d8',
  overtime_afternoon: '#ce93d8',
  overtime_full_day: '#7b1fa2',
  overtime_comp_off: '#ff7043',
}

// Grid cell abbreviations per contracts §2.4 (CSWD-127 T001)
const STATUS_ABBR: Record<string, string> = {
  on_workday: '上班',
  on_nonworkday: '值班',
  off_workday: '调休',
  off_nonworkday: '休',
  vacation: '休假',
  leave_morning: '假上',
  leave_afternoon: '假下',
  leave_full_day: '假',
  overtime: '加班',
  overtime_morning: '加上',
  overtime_afternoon: '加下',
  overtime_full_day: '加天',
  overtime_comp_off: '补休',
}

// Weekday i18n keys indexed by JS getDay() (0=Sun … 6=Sat)
const WEEKDAY_KEYS = [
  'dsGridWeekdaySun',
  'dsGridWeekdayMon',
  'dsGridWeekdayTue',
  'dsGridWeekdayWed',
  'dsGridWeekdayThu',
  'dsGridWeekdayFri',
  'dsGridWeekdaySat',
]

// Group display order per contracts §2.5
const GROUP_ORDER = ['CN-A', 'CN-B', 'US']

function groupedMembers(members: MemberInfo[]): { group: string; members: MemberInfo[] }[] {
  const groupMap = new Map<string, MemberInfo[]>()
  for (const m of members) {
    const g = m.group_name || 'Unknown'
    if (!groupMap.has(g)) groupMap.set(g, [])
    groupMap.get(g)!.push(m)
  }
  const result: { group: string; members: MemberInfo[] }[] = []
  for (const g of GROUP_ORDER) {
    if (groupMap.has(g)) {
      result.push({ group: g, members: groupMap.get(g)! })
      groupMap.delete(g)
    }
  }
  for (const [g, ms] of groupMap) {
    result.push({ group: g, members: ms })
  }
  return result
}

// Green (eye-friendly) mode — colors with good contrast on light green background
const STATUS_COLOR_GREEN: Record<string, string> = {
  on_workday: '#2e7d32',
  on_nonworkday: '#558b2f',
  off_workday: '#e65100',
  off_nonworkday: '#546e7a',
  vacation: '#0d47a1',
  leave_morning: '#c2185b',
  leave_afternoon: '#c2185b',
  leave_full_day: '#880e4f',
  overtime: '#6a1b9a',
  overtime_morning: '#9c27b0',
  overtime_afternoon: '#9c27b0',
  overtime_full_day: '#4a148c',
  overtime_comp_off: '#bf360c',
}

function getStatusColors(theme: ThemeMode): Record<string, string> {
  return theme === 'green' ? STATUS_COLOR_GREEN : STATUS_COLOR
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function authHeader(token: string | null): Record<string, string> {
  if (!token) return {}
  return { Authorization: `Bearer ${token}` }
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function isoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function currentYearMonth(): string {
  const now = new Date()
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`
}

function statusLabelKey(rs: string): string {
  const map: Record<string, string> = {
    on_workday: 'dsStatusOnWorkday',
    on_nonworkday: 'dsStatusOnNonworkday',
    off_workday: 'dsStatusOffWorkday',
    off_nonworkday: 'dsStatusOffNonworkday',
    vacation: 'dsStatusVacation',
    leave_morning: 'dsStatusLeaveMorning',
    leave_afternoon: 'dsStatusLeaveAfternoon',
    leave_full_day: 'dsStatusLeaveFullDay',
    overtime: 'dsStatusOvertime',
    overtime_morning: 'dsStatusOvertimeMorning',
    overtime_afternoon: 'dsStatusOvertimeAfternoon',
    overtime_full_day: 'dsStatusOvertimeFullDay',
    overtime_comp_off: 'dsStatusOvertimeCompOff',
  }
  return map[rs] ?? rs
}

// ── DutySchedulePage ─────────────────────────────────────────────────────────

export function DutySchedulePage() {
  const { t } = useLang()
  const { user, accessToken } = useAuth()
  const [activeTab, setActiveTab] = useState<'holiday' | 'import' | 'grid' | 'stats'>('holiday')

  const isManagerOrAdmin = user && (user.role === 'manager' || user.role === 'admin')

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', color: 'var(--text-primary)' }}>
      <h2>{t('navDutySchedule')}</h2>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid var(--border)' }}>
        <TabButton active={activeTab === 'holiday'} onClick={() => setActiveTab('holiday')}>
          {t('dsHolidayCalendar')}
        </TabButton>
        {isManagerOrAdmin && (
          <>
            <TabButton active={activeTab === 'import'} onClick={() => setActiveTab('import')}>
              {t('dsImportTitle')}
            </TabButton>
            <TabButton active={activeTab === 'grid'} onClick={() => setActiveTab('grid')}>
              {t('dsGridTitle')}
            </TabButton>
            <TabButton active={activeTab === 'stats'} onClick={() => setActiveTab('stats')}>
              {t('dsMonthlyStatsTitle')}
            </TabButton>
          </>
        )}
      </div>

      {activeTab === 'holiday' && (
        <HolidayCalendarTab token={accessToken} isManagerOrAdmin={!!isManagerOrAdmin} t={t} />
      )}
      {activeTab === 'import' && isManagerOrAdmin && (
        <ScheduleImportTab token={accessToken} t={t} />
      )}
      {activeTab === 'grid' && isManagerOrAdmin && (
        <ScheduleGridTab token={accessToken} user={user} t={t} />
      )}
      {activeTab === 'stats' && isManagerOrAdmin && (
        <MonthlyStatsTab token={accessToken} t={t} />
      )}
    </div>
  )
}

// ── TabButton ────────────────────────────────────────────────────────────────

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 16px',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        color: active ? 'var(--accent)' : 'var(--text-secondary)',
        fontWeight: active ? 600 : 400,
        fontSize: '14px',
      }}
    >
      {children}
    </button>
  )
}

// ── ScheduleGridTab ──────────────────────────────────────────────────────────

function ScheduleGridTab({
  token,
  t,
}: {
  token: string | null
  user?: { role: string } | null
  t: (k: string) => string
}) {
  const { theme } = usePrefs()
  const statusColors = getStatusColors(theme)

  const [month, setMonth] = useState(currentYearMonth())
  const [cells, setCells] = useState<ScheduleCell[]>([])
  const [members, setMembers] = useState<MemberInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState('')
  const [msgIsSuccess, setMsgIsSuccess] = useState(true)

  // Batch selection state
  const [selected, setSelected] = useState<Set<string>>(new Set()) // "member_id|date"
  const [batchStatus, setBatchStatus] = useState<RawStatus>('on_workday')
  const [batchApplying, setBatchApplying] = useState(false)

  // Status dropdown: which cell is open
  const [openCell, setOpenCell] = useState<string | null>(null) // "member_id|date"
  const [leaveSubmenu, setLeaveSubmenu] = useState(false)
  const [overtimeSubmenu, setOvertimeSubmenu] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [schedRes, rosterRes] = await Promise.all([
        fetch(`/api/duty-schedule?month=${month}`, { headers: authHeader(token) }),
        fetch('/api/duty-schedule/roster', { headers: authHeader(token) }),
      ])
      const schedData = schedRes.ok ? await schedRes.json() : { data: [] }
      const rosterData = rosterRes.ok ? await rosterRes.json() : { data: [] }
      setCells(schedData.data ?? [])
      setMembers(rosterData.data ?? [])
    } finally {
      setLoading(false)
    }
  }, [token, month])

  useEffect(() => { loadData() }, [loadData])

  // Build cell map: "member_id|date" → ScheduleCell
  const cellMap = new Map<string, ScheduleCell>()
  for (const c of cells) {
    cellMap.set(`${c.member_id}|${c.date}`, c)
  }

  // Month grid dimensions
  const year = parseInt(month.slice(0, 4), 10)
  const mon = parseInt(month.slice(4, 6), 10) - 1
  const numDays = daysInMonth(year, mon)
  const days = Array.from({ length: numDays }, (_, i) => i + 1)

  async function saveCell(memberId: string, dateStr: string, rawStatus: string): Promise<{ ok: boolean; errorMsg?: string }> {
    const r = await fetch('/api/duty-schedule/cell', {
      method: 'PUT',
      headers: { ...authHeader(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ member_id: memberId, date: dateStr, raw_status: rawStatus }),
    })
    if (r.ok) {
      const saved = await r.json()
      setCells(prev => {
        const key = `${memberId}|${dateStr}`
        const existing = prev.find(c => `${c.member_id}|${c.date}` === key)
        if (existing) {
          return prev.map(c =>
            `${c.member_id}|${c.date}` === key
              ? { ...c, raw_status: rawStatus, is_working: saved.is_working, source: 'manual' }
              : c
          )
        }
        return [...prev, {
          id: saved.id,
          member_id: memberId,
          member_name: members.find(m => m.id === memberId)?.display_name ?? null,
          date: dateStr,
          raw_status: rawStatus,
          is_working: saved.is_working,
          source: 'manual',
        }]
      })
      return { ok: true }
    }
    let errorMsg = ''
    try {
      const d = await r.json()
      errorMsg = d?.error?.message ?? ''
    } catch { /* ignore parse error */ }
    return { ok: false, errorMsg }
  }

  function handleCellClick(memberId: string, dateStr: string, shiftKey: boolean) {
    const key = `${memberId}|${dateStr}`
    if (shiftKey) {
      setSelected(prev => {
        const next = new Set(prev)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      })
    } else {
      setSelected(new Set())
      setOpenCell(prev => (prev === key ? null : key))
      setLeaveSubmenu(false)
      setOvertimeSubmenu(false)
    }
  }

  async function handleStatusSelect(memberId: string, dateStr: string, rawStatus: string) {
    setOpenCell(null)
    const result = await saveCell(memberId, dateStr, rawStatus)
    if (result.ok) {
      setMsg(t('dsGridSaved'))
      setMsgIsSuccess(true)
    } else {
      setMsg(result.errorMsg ? `${t('dsGridSaveError')}: ${result.errorMsg}` : t('dsGridSaveError'))
      setMsgIsSuccess(false)
    }
    setTimeout(() => setMsg(''), 2000)
  }

  async function applyBatchStatus() {
    if (selected.size === 0) return
    setBatchApplying(true)
    let ok = 0
    for (const key of selected) {
      const [memberId, dateStr] = key.split('|')
      const result = await saveCell(memberId, dateStr, batchStatus)
      if (result.ok) ok++
    }
    setBatchApplying(false)
    setSelected(new Set())
    setMsg(`${t('dsGridSaved')} (${ok}/${selected.size})`)
    setMsgIsSuccess(true)
    setTimeout(() => setMsg(''), 3000)
  }

  const monthInput = `${year}-${String(mon + 1).padStart(2, '0')}`

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
          {t('dsGridMonth')}
          <input
            type="month"
            value={monthInput}
            onChange={e => {
              const v = e.target.value.replace('-', '')
              setMonth(v)
              setSelected(new Set())
              setOpenCell(null)
            }}
            style={{ padding: '4px 8px', fontSize: '14px', background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '4px' }}
          />
        </label>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('dsGridBatchHint')}</span>
      </div>

      {/* Batch controls */}
      {selected.size > 0 && (
        <div style={{
          display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px',
          padding: '10px 16px', background: 'var(--info-bg)', borderRadius: '6px', flexWrap: 'wrap',
          border: '1px solid var(--border)',
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
            {t('dsGridBatchSetStatus')} ({selected.size})
          </span>
          <StatusDropdownInline
            value={batchStatus}
            onChange={v => setBatchStatus(v as RawStatus)}
            t={t}
          />
          <button
            onClick={applyBatchStatus}
            disabled={batchApplying}
            style={{ padding: '5px 16px', background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            {batchApplying ? '...' : t('dsGridBatchApply')}
          </button>
          <button
            onClick={() => setSelected(new Set())}
            style={{ padding: '5px 14px', background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer' }}
          >
            {t('dsGridClearSelection')}
          </button>
        </div>
      )}

      {msg && (
        <div style={{
          marginBottom: '10px', padding: '8px 12px',
          background: msgIsSuccess ? 'var(--success-bg)' : 'var(--danger-bg)',
          border: `1px solid ${msgIsSuccess ? 'var(--success)' : 'var(--danger)'}`,
          borderRadius: '4px',
          color: msgIsSuccess ? 'var(--success)' : 'var(--danger)',
          fontSize: '13px',
        }}>
          {msg}
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('dsGridLoading')}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '600px', background: 'var(--bg-surface)' }}>
            <thead>
              <tr style={{ background: 'var(--bg-page)', color: 'var(--text-secondary)' }}>
                <th style={{ ...TH, minWidth: '110px', position: 'sticky', left: 0, background: 'var(--bg-page)', zIndex: 2, borderBottom: '1px solid var(--border)' }}>
                  {t('dsGridColMember')}
                </th>
                {days.map(d => {
                  const wd = t(WEEKDAY_KEYS[new Date(year, mon, d).getDay()])
                  return (
                    <th key={d} style={{ ...TH, width: '36px', textAlign: 'center', padding: '2px 2px', borderBottom: '1px solid var(--border)', lineHeight: '1.3' }}>
                      <div style={{ fontSize: '11px' }}>{`${mon + 1}/${d}`}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 400 }}>{wd}</div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr>
                  <td colSpan={numDays + 1} style={{ ...TD, textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>
                    {t('dsGridNoData')}
                  </td>
                </tr>
              ) : (
                groupedMembers(members).flatMap(({ group, members: groupMs }) => [
                  <tr key={`grp-${group}`}>
                    <td
                      colSpan={numDays + 1}
                      data-group-header={group}
                      style={{
                        padding: '3px 8px',
                        fontSize: '11px',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        color: 'var(--text-secondary)',
                        background: 'var(--bg-page)',
                        borderTop: '1px solid var(--border)',
                        borderBottom: '1px solid var(--border)',
                      }}
                    >
                      {group}
                    </td>
                  </tr>,
                  ...groupMs.map(m => (
                    <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ ...TD, minWidth: '110px', fontWeight: 500, position: 'sticky', left: 0, background: 'var(--bg-surface)', zIndex: 1, whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>
                        {m.display_name}
                      </td>
                      {days.map(d => {
                        const dateStr = isoDate(year, mon, d)
                        const key = `${m.id}|${dateStr}`
                        const cell = cellMap.get(key)
                        const isSelected = selected.has(key)
                        const isOpen = openCell === key
                        const cellColor = cell ? statusColors[cell.raw_status] : undefined

                        return (
                          <td
                            key={d}
                            onClick={e => handleCellClick(m.id, dateStr, e.shiftKey)}
                            style={{
                              width: '36px', height: '32px',
                              padding: '0', textAlign: 'center',
                              cursor: 'pointer',
                              background: isSelected
                                ? 'var(--info-bg)'
                                : cell
                                  ? cellColor
                                  : 'transparent',
                              border: isSelected ? '1px solid var(--accent)' : '1px solid transparent',
                              position: 'relative',
                            }}
                          >
                            {cell && !isSelected && (
                              <span style={{
                                display: 'block',
                                lineHeight: '32px',
                                fontSize: '10px',
                                fontWeight: 700,
                                color: '#fff',
                                userSelect: 'none',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }} title={t(statusLabelKey(cell.raw_status))}>
                                {STATUS_ABBR[cell.raw_status] ?? cell.raw_status}
                              </span>
                            )}
                            {isOpen && (
                              <StatusDropdownPopup
                                onSelect={rs => handleStatusSelect(m.id, dateStr, rs)}
                                onClose={() => { setOpenCell(null); setLeaveSubmenu(false); setOvertimeSubmenu(false) }}
                                leaveSubmenu={leaveSubmenu}
                                setLeaveSubmenu={setLeaveSubmenu}
                                overtimeSubmenu={overtimeSubmenu}
                                setOvertimeSubmenu={setOvertimeSubmenu}
                                t={t}
                              />
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  )),
                ])
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Color legend */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px', fontSize: '12px', color: 'var(--text-secondary)' }}>
        {ALL_STATUSES.map(rs => (
          <span key={rs} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: statusColors[rs], display: 'inline-block' }} />
            {t(statusLabelKey(rs))}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── StatusDropdownPopup ───────────────────────────────────────────────────────

function StatusDropdownPopup({
  onSelect,
  onClose,
  leaveSubmenu,
  setLeaveSubmenu,
  overtimeSubmenu,
  setOvertimeSubmenu,
  t,
}: {
  onSelect: (rs: string) => void
  onClose: () => void
  leaveSubmenu: boolean
  setLeaveSubmenu: (v: boolean) => void
  overtimeSubmenu: boolean
  setOvertimeSubmenu: (v: boolean) => void
  t: (k: string) => string
}) {
  const { theme } = usePrefs()
  const statusColors = getStatusColors(theme)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const simpleItems: [string, string][] = [
    ['on_workday', 'dsStatusOnWorkday'],
    ['on_nonworkday', 'dsStatusOnNonworkday'],
    ['off_workday', 'dsStatusOffWorkday'],
    ['off_nonworkday', 'dsStatusOffNonworkday'],
    ['vacation', 'dsStatusVacation'],
    ['overtime', 'dsStatusOvertime'],
    ['overtime_comp_off', 'dsStatusOvertimeCompOff'],
  ]

  const popupStyle: CSSProperties = {
    position: 'absolute', top: '100%', left: 0, zIndex: 100,
    background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)', minWidth: '140px',
    fontSize: '13px', color: 'var(--text-primary)',
  }

  const submenuStyle: CSSProperties = {
    position: 'absolute', top: 0, left: '100%',
    background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)', minWidth: '140px',
    zIndex: 101,
  }

  const itemStyle: CSSProperties = {
    padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
  }

  function onEnter(e: React.MouseEvent<HTMLDivElement>) {
    e.currentTarget.style.background = 'var(--bg-page)'
  }
  function onLeave(e: React.MouseEvent<HTMLDivElement>) {
    e.currentTarget.style.background = 'transparent'
  }

  return (
    <div
      ref={ref}
      onClick={e => e.stopPropagation()}
      style={popupStyle}
    >
      {simpleItems.map(([rs, key]) => (
        <div
          key={rs}
          onClick={() => onSelect(rs)}
          style={itemStyle}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        >
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColors[rs], flexShrink: 0 }} />
          {t(key)}
        </div>
      ))}

      {/* 请假 submenu */}
      <div style={{ position: 'relative' }}>
        <div
          onClick={() => { setLeaveSubmenu(!leaveSubmenu); setOvertimeSubmenu(false) }}
          style={{ ...itemStyle, justifyContent: 'space-between' }}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColors['leave_full_day'], flexShrink: 0 }} />
            {t('dsStatusLeaveSubmenu')}
          </span>
          <span>▶</span>
        </div>
        {leaveSubmenu && (
          <div style={submenuStyle}>
            {(['leave_morning', 'leave_afternoon', 'leave_full_day'] as const).map(rs => (
              <div
                key={rs}
                onClick={() => onSelect(rs)}
                style={itemStyle}
                onMouseEnter={onEnter}
                onMouseLeave={onLeave}
              >
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColors[rs], flexShrink: 0 }} />
                {t(statusLabelKey(rs))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 加班 submenu */}
      <div style={{ position: 'relative' }}>
        <div
          onClick={() => { setOvertimeSubmenu(!overtimeSubmenu); setLeaveSubmenu(false) }}
          style={{ ...itemStyle, justifyContent: 'space-between' }}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColors['overtime_full_day'], flexShrink: 0 }} />
            {t('dsStatusOvertimeSubmenu')}
          </span>
          <span>▶</span>
        </div>
        {overtimeSubmenu && (
          <div style={submenuStyle}>
            {(['overtime_morning', 'overtime_afternoon', 'overtime_full_day'] as const).map(rs => (
              <div
                key={rs}
                onClick={() => onSelect(rs)}
                style={itemStyle}
                onMouseEnter={onEnter}
                onMouseLeave={onLeave}
              >
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColors[rs], flexShrink: 0 }} />
                {t(statusLabelKey(rs))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── StatusDropdownInline ──────────────────────────────────────────────────────

function StatusDropdownInline({
  value,
  onChange,
  t,
}: {
  value: string
  onChange: (v: string) => void
  t: (k: string) => string
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        padding: '4px 8px', fontSize: '13px', borderRadius: '4px',
        border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)',
      }}
    >
      {ALL_STATUSES.map(rs => (
        <option key={rs} value={rs}>{t(statusLabelKey(rs))}</option>
      ))}
    </select>
  )
}

// ── MonthlyStatsTab ──────────────────────────────────────────────────────────

function MonthlyStatsTab({
  token,
  t,
}: {
  token: string | null
  t: (k: string) => string
}) {
  const [month, setMonth] = useState(currentYearMonth())
  const [stats, setStats] = useState<MonthlyStatRow[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await fetch(`/api/duty-schedule/monthly-stats?month=${month}`, {
        headers: authHeader(token),
      })
      const d = r.ok ? await r.json() : { data: [] }
      setStats(d.data ?? [])
    } finally {
      setLoading(false)
    }
  }, [token, month])

  useEffect(() => { load() }, [load])

  const year = parseInt(month.slice(0, 4), 10)
  const mon = parseInt(month.slice(4, 6), 10) - 1
  const monthInput = `${year}-${String(mon + 1).padStart(2, '0')}`

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
        <label style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
          {t('dsGridMonth')}
          <input
            type="month"
            value={monthInput}
            onChange={e => setMonth(e.target.value.replace('-', ''))}
            style={{ padding: '4px 8px', fontSize: '14px', background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '4px' }}
          />
        </label>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('dsGridLoading')}</p>
      ) : stats.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('dsStatsNoData')}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '13px', width: '100%', background: 'var(--bg-surface)' }}>
            <thead>
              <tr style={{ background: 'var(--bg-page)', color: 'var(--text-secondary)' }}>
                <th style={{ ...TH, borderBottom: '1px solid var(--border)' }}>{t('dsGridColMember')}</th>
                <th style={{ ...TH, textAlign: 'center', borderBottom: '1px solid var(--border)' }}>{t('dsStatsOnWorkday')}</th>
                <th style={{ ...TH, textAlign: 'center', borderBottom: '1px solid var(--border)' }}>{t('dsStatsOnNonworkday')}</th>
                <th style={{ ...TH, textAlign: 'center', borderBottom: '1px solid var(--border)' }}>{t('dsStatsOffWorkday')}</th>
                <th style={{ ...TH, textAlign: 'center', borderBottom: '1px solid var(--border)' }}>{t('dsStatsOffNonworkday')}</th>
                <th style={{ ...TH, textAlign: 'center', borderBottom: '1px solid var(--border)' }}>{t('dsStatsVacation')}</th>
                <th style={{ ...TH, textAlign: 'center', borderBottom: '1px solid var(--border)' }}>{t('dsStatsLeave')}</th>
                <th style={{ ...TH, textAlign: 'center', borderBottom: '1px solid var(--border)' }}>{t('dsStatsOvertime')}</th>
                <th style={{ ...TH, textAlign: 'center', borderBottom: '1px solid var(--border)' }}>{t('dsStatsOvertimeCompOff')}</th>
                <th style={{ ...TH, textAlign: 'center', fontWeight: 700, borderBottom: '1px solid var(--border)' }}>{t('dsTotalOnDuty')}</th>
              </tr>
            </thead>
            <tbody>
              {stats.map(row => (
                <tr key={row.member_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ ...TD, color: 'var(--text-primary)' }}>{row.member_name}</td>
                  <td style={{ ...TD, textAlign: 'center', color: 'var(--text-primary)' }}>{row.on_workday}</td>
                  <td style={{ ...TD, textAlign: 'center', color: 'var(--text-primary)' }}>{row.on_nonworkday}</td>
                  <td style={{ ...TD, textAlign: 'center', color: 'var(--text-primary)' }}>{row.off_workday}</td>
                  <td style={{ ...TD, textAlign: 'center', color: 'var(--text-primary)' }}>{row.off_nonworkday}</td>
                  <td style={{ ...TD, textAlign: 'center', color: 'var(--text-primary)' }}>{row.vacation}</td>
                  <td style={{ ...TD, textAlign: 'center', color: 'var(--text-primary)' }}>{row.leave.toFixed(1)}</td>
                  <td style={{ ...TD, textAlign: 'center', color: 'var(--text-primary)' }}>{row.overtime.toFixed(1)}</td>
                  <td style={{ ...TD, textAlign: 'center', color: 'var(--text-primary)' }}>{row.overtime_comp_off}</td>
                  <td style={{ ...TD, textAlign: 'center', fontWeight: 700, color: 'var(--text-primary)' }}>{row.total_on_duty.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── HolidayCalendarTab ───────────────────────────────────────────────────────

function HolidayCalendarTab({
  token,
  isManagerOrAdmin,
  t,
}: {
  token: string | null
  isManagerOrAdmin: boolean
  t: (k: string) => string
}) {
  const [holidays, setHolidays] = useState<HolidayEntry[]>([])
  const [regionFilter, setRegionFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [editType, setEditType] = useState('')
  const [editName, setEditName] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [addDate, setAddDate] = useState('')
  const [addRegion, setAddRegion] = useState('CN')
  const [addType, setAddType] = useState('holiday')
  const [addName, setAddName] = useState('')
  const [msg, setMsg] = useState('')
  const importRef = useRef<HTMLInputElement>(null)

  const load = useCallback(() => {
    setLoading(true)
    const url = regionFilter
      ? `/api/holiday-calendar?region=${regionFilter}`
      : '/api/holiday-calendar'
    fetch(url, { headers: authHeader(token) })
      .then(r => r.json())
      .then(d => setHolidays(d.data ?? []))
      .finally(() => setLoading(false))
  }, [token, regionFilter])

  useEffect(() => { load() }, [load])

  const deleteEntry = async (id: string) => {
    if (!window.confirm(t('holidayDeleteConfirm'))) return
    const r = await fetch(`/api/holiday-calendar/${id}`, {
      method: 'DELETE',
      headers: authHeader(token),
    })
    if (r.ok) {
      setMsg(t('holidayDeleted'))
      load()
    } else {
      const e = await r.json()
      setMsg(e?.error?.message ?? t('errorGeneric'))
    }
  }

  const startEdit = (h: HolidayEntry) => {
    setEditId(h.id)
    setEditType(h.type)
    setEditName(h.name ?? '')
  }

  const saveEdit = async () => {
    if (!editId) return
    const r = await fetch(`/api/holiday-calendar/${editId}`, {
      method: 'PUT',
      headers: { ...authHeader(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: editType, name: editName || null }),
    })
    if (r.ok) {
      setMsg(t('holidaySaved'))
      setEditId(null)
      load()
    } else {
      const e = await r.json()
      setMsg(e?.error?.message ?? t('errorGeneric'))
    }
  }

  const addEntry = async () => {
    if (!addDate) { setMsg(t('holidayDateRequired')); return }
    const r = await fetch('/api/holiday-calendar', {
      method: 'POST',
      headers: { ...authHeader(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        date: addDate,
        region_code: addRegion,
        type: addType,
        name: addName || null,
      }),
    })
    if (r.ok) {
      setMsg(t('holidayAdded'))
      setShowAdd(false)
      setAddDate('')
      setAddName('')
      load()
    } else {
      const e = await r.json()
      setMsg(e?.error?.message ?? t('errorGeneric'))
    }
  }

  const importHolidays = async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    const r = await fetch('/api/holiday-calendar/import', {
      method: 'POST',
      headers: authHeader(token),
      body: formData,
    })
    const d = await r.json()
    if (r.ok) {
      const skippedCount = Array.isArray(d.skipped) ? d.skipped.length : 0
      setMsg(
        t('holidayImportSuccess')
          .replace('{added}', String(d.upserted))
          .replace('{skipped}', String(skippedCount))
      )
      load()
    } else {
      setMsg(d?.error?.message ?? t('errorGeneric'))
    }
  }

  const typeLabel = (type: string) =>
    type === 'holiday' ? t('holidayTypeHoliday') : t('holidayTypeMakeup')

  const inputSt: CSSProperties = {
    padding: '4px 8px', background: 'var(--bg-surface)', color: 'var(--text-primary)',
    border: '1px solid var(--border)', borderRadius: '4px',
  }
  const selectSt: CSSProperties = { ...inputSt }

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
          {t('holidayRegionFilter')}
          <select
            value={regionFilter}
            onChange={e => setRegionFilter(e.target.value)}
            style={{ ...selectSt, marginLeft: '8px' }}
          >
            <option value="">{t('holidayRegionAll')}</option>
            <option value="CN">{t('holidayRegionCN')}</option>
            <option value="US">{t('holidayRegionUS')}</option>
          </select>
        </label>

        {isManagerOrAdmin && (
          <>
            <button
              onClick={() => setShowAdd(!showAdd)}
              style={{ padding: '6px 14px', cursor: 'pointer', background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none', borderRadius: '4px' }}
            >
              {t('holidayAddEntry')}
            </button>
            <button
              onClick={() => importRef.current?.click()}
              style={{ padding: '6px 14px', cursor: 'pointer', background: 'var(--success)', color: 'var(--accent-contrast)', border: 'none', borderRadius: '4px' }}
            >
              {t('holidayImportXlsx')}
            </button>
            <input
              ref={importRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files?.[0]
                if (f) { importHolidays(f); e.target.value = '' }
              }}
            />
          </>
        )}
      </div>

      {msg && (
        <div style={{ marginBottom: '12px', padding: '8px 12px', background: 'var(--success-bg)', border: '1px solid var(--success)', borderRadius: '4px', fontSize: '13px', color: 'var(--success)' }}>
          {msg}
          <button onClick={() => setMsg('')} style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--success)' }}>✕</button>
        </div>
      )}

      {showAdd && isManagerOrAdmin && (
        <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--bg-surface)' }}>
          <h4 style={{ margin: '0 0 12px', color: 'var(--text-primary)' }}>{t('holidayAddEntry')}</h4>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
              {t('holidayColDate')}
              <input type="date" value={addDate} onChange={e => setAddDate(e.target.value)}
                style={{ ...inputSt, marginLeft: '6px' }} />
            </label>
            <label style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
              {t('holidayColRegion')}
              <select value={addRegion} onChange={e => setAddRegion(e.target.value)}
                style={{ ...selectSt, marginLeft: '6px' }}>
                <option value="CN">CN</option>
                <option value="US">US</option>
              </select>
            </label>
            <label style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
              {t('holidayColType')}
              <select value={addType} onChange={e => setAddType(e.target.value)}
                style={{ ...selectSt, marginLeft: '6px' }}>
                <option value="holiday">{t('holidayTypeHoliday')}</option>
                <option value="makeup_workday">{t('holidayTypeMakeup')}</option>
              </select>
            </label>
            <label style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
              {t('holidayColName')}
              <input value={addName} onChange={e => setAddName(e.target.value)}
                placeholder={t('holidayNamePlaceholder')}
                style={{ ...inputSt, marginLeft: '6px', width: '150px' }} />
            </label>
            <button onClick={addEntry}
              style={{ padding: '5px 14px', background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              {t('holidaySave')}
            </button>
            <button onClick={() => setShowAdd(false)}
              style={{ padding: '5px 14px', background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer' }}>
              {t('holidayCancel')}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('loading')}</p>
      ) : holidays.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('holidayNoData')}</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', background: 'var(--bg-surface)' }}>
          <thead>
            <tr style={{ background: 'var(--bg-page)', color: 'var(--text-secondary)' }}>
              <th style={{ ...TH, borderBottom: '1px solid var(--border)' }}>{t('holidayColDate')}</th>
              <th style={{ ...TH, borderBottom: '1px solid var(--border)' }}>{t('holidayColRegion')}</th>
              <th style={{ ...TH, borderBottom: '1px solid var(--border)' }}>{t('holidayColType')}</th>
              <th style={{ ...TH, borderBottom: '1px solid var(--border)' }}>{t('holidayColName')}</th>
              {isManagerOrAdmin && <th style={{ ...TH, borderBottom: '1px solid var(--border)' }}>{t('holidayActions')}</th>}
            </tr>
          </thead>
          <tbody>
            {holidays.map(h => (
              <tr key={h.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ ...TD, color: 'var(--text-primary)' }}>{h.date}</td>
                <td style={{ ...TD, color: 'var(--text-primary)' }}>{h.region_code}</td>
                <td style={{ ...TD }}>
                  {editId === h.id ? (
                    <select value={editType} onChange={e => setEditType(e.target.value)}
                      style={{ ...selectSt, padding: '3px 6px' }}>
                      <option value="holiday">{t('holidayTypeHoliday')}</option>
                      <option value="makeup_workday">{t('holidayTypeMakeup')}</option>
                    </select>
                  ) : (
                    <span style={{
                      background: h.type === 'holiday' ? 'var(--info-bg)' : 'var(--warning-bg)',
                      color: h.type === 'holiday' ? 'var(--info)' : 'var(--warning)',
                      padding: '2px 8px', borderRadius: '4px',
                    }}>
                      {typeLabel(h.type)}
                    </span>
                  )}
                </td>
                <td style={{ ...TD, color: 'var(--text-primary)' }}>
                  {editId === h.id ? (
                    <input value={editName} onChange={e => setEditName(e.target.value)}
                      style={{ ...inputSt, padding: '3px 6px', width: '140px' }} />
                  ) : (
                    h.name ?? '—'
                  )}
                </td>
                {isManagerOrAdmin && (
                  <td style={TD}>
                    {editId === h.id ? (
                      <>
                        <button onClick={saveEdit}
                          style={{ marginRight: '6px', padding: '3px 10px', background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>
                          {t('holidaySave')}
                        </button>
                        <button onClick={() => setEditId(null)}
                          style={{ padding: '3px 10px', background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '3px', cursor: 'pointer' }}>
                          {t('holidayCancel')}
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(h)}
                          style={{ marginRight: '6px', padding: '3px 10px', border: '1px solid var(--accent)', color: 'var(--accent)', background: 'none', borderRadius: '3px', cursor: 'pointer' }}>
                          {t('holidayEdit')}
                        </button>
                        <button onClick={() => deleteEntry(h.id)}
                          style={{ padding: '3px 10px', border: '1px solid var(--danger)', color: 'var(--danger)', background: 'none', borderRadius: '3px', cursor: 'pointer' }}>
                          {t('holidayDelete')}
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-muted)' }}>
        {t('holidayTotal')}: {holidays.length}
      </div>
    </div>
  )
}

// ── ScheduleImportTab ────────────────────────────────────────────────────────

function ScheduleImportTab({
  token,
  t,
}: {
  token: string | null
  t: (k: string) => string
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [error, setError] = useState('')

  const uploadFile = async (file: File) => {
    setUploading(true)
    setError('')
    setDryRun(null)
    setSuccessMsg('')

    const formData = new FormData()
    formData.append('file', file)

    const r = await fetch('/api/duty-schedule/import', {
      method: 'POST',
      headers: authHeader(token),
      body: formData,
    })
    const d = await r.json()
    setUploading(false)

    if (r.ok) {
      setDryRun(d)
    } else {
      setError(d?.error?.message ?? t('errorGeneric'))
    }
  }

  const confirmImport = async () => {
    if (!dryRun) return
    setConfirming(true)
    setError('')

    const r = await fetch('/api/duty-schedule/import/confirm', {
      method: 'POST',
      headers: { ...authHeader(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        batch_id: dryRun.batch_id,
        rows: dryRun.rows,
      }),
    })
    const d = await r.json()
    setConfirming(false)

    if (r.ok) {
      setSuccessMsg(t('dsImportSuccess').replace('{n}', String(d.upserted)))
      setDryRun(null)
    } else {
      setError(d?.error?.message ?? t('errorGeneric'))
    }
  }

  return (
    <div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '16px' }}>
        {t('dsImportHint')}
      </p>

      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{ padding: '8px 18px', background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none', borderRadius: '4px', cursor: uploading ? 'not-allowed' : 'pointer' }}
        >
          {uploading ? t('loading') : t('dsImportUpload')}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={e => {
            const f = e.target.files?.[0]
            if (f) { uploadFile(f); e.target.value = '' }
          }}
        />
      </div>

      {error && (
        <div style={{ marginBottom: '12px', padding: '8px 12px', background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: '4px', color: 'var(--danger)', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {successMsg && (
        <div style={{ marginBottom: '12px', padding: '8px 12px', background: 'var(--success-bg)', border: '1px solid var(--success)', borderRadius: '4px', color: 'var(--success)', fontSize: '13px' }}>
          {successMsg}
        </div>
      )}

      {dryRun && (
        <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '16px', background: 'var(--bg-surface)' }}>
          <h3 style={{ margin: '0 0 12px', color: 'var(--text-primary)' }}>{t('dsImportPreview')}</h3>

          <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <Stat label={t('dsImportMembers')} value={dryRun.members} />
            <Stat label={t('dsImportDays')} value={dryRun.days} />
            <Stat label={t('dsImportAdded')} value={dryRun.added} color="var(--success)" />
            <Stat label={t('dsImportOverridden')} value={dryRun.overridden} color="var(--warning)" />
            <Stat label={t('dsImportWarnings')} value={dryRun.warnings.length} color={dryRun.warnings.length > 0 ? 'var(--danger)' : undefined} />
          </div>

          {dryRun.warnings.length > 0 && (
            <details style={{ marginBottom: '12px' }}>
              <summary style={{ cursor: 'pointer', fontSize: '13px', color: 'var(--danger)' }}>
                {t('dsImportWarnings')} ({dryRun.warnings.length})
              </summary>
              <ul style={{ margin: '8px 0 0 0', padding: '0 0 0 18px', fontSize: '12px', color: 'var(--text-secondary)', maxHeight: '200px', overflowY: 'auto' }}>
                {dryRun.warnings.map((w, i) => (
                  <li key={i}>{w.message}</li>
                ))}
              </ul>
            </details>
          )}

          {dryRun.rows.length > 0 ? (
            <div style={{ marginBottom: '16px', maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', background: 'var(--bg-surface)' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-page)', color: 'var(--text-secondary)' }}>
                    <th style={{ ...TH, borderBottom: '1px solid var(--border)' }}>{t('dsImportColMember')}</th>
                    <th style={{ ...TH, borderBottom: '1px solid var(--border)' }}>{t('dsImportColDate')}</th>
                    <th style={{ ...TH, borderBottom: '1px solid var(--border)' }}>{t('dsImportColStatus')}</th>
                    <th style={{ ...TH, borderBottom: '1px solid var(--border)' }}>{t('dsImportColAction')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dryRun.rows.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ ...TD, color: 'var(--text-primary)' }}>{r.member_name}</td>
                      <td style={{ ...TD, color: 'var(--text-primary)' }}>{r.date}</td>
                      <td style={TD}>
                        <code style={{ fontSize: '12px', background: 'var(--bg-elevated)', color: 'var(--text-primary)', padding: '1px 4px', borderRadius: '3px' }}>{r.raw_status}</code>
                      </td>
                      <td style={TD}>
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: r.is_override ? 'var(--warning-bg)' : 'var(--success-bg)',
                          color: r.is_override ? 'var(--warning)' : 'var(--success)',
                        }}>
                          {r.is_override ? t('dsImportOverrideLabel') : t('dsImportNewLabel')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{t('dsImportNoRows')}</p>
          )}

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={confirmImport}
              disabled={confirming || dryRun.rows.length === 0}
              style={{
                padding: '8px 20px',
                background: dryRun.rows.length === 0 ? 'var(--bg-elevated)' : 'var(--accent)',
                color: dryRun.rows.length === 0 ? 'var(--text-muted)' : 'var(--accent-contrast)',
                border: 'none',
                borderRadius: '4px',
                cursor: dryRun.rows.length === 0 || confirming ? 'not-allowed' : 'pointer',
              }}
            >
              {confirming ? t('loading') : t('dsImportConfirm')}
            </button>
            <button
              onClick={() => setDryRun(null)}
              style={{ padding: '8px 20px', background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer' }}
            >
              {t('dsImportCancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function Stat({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '24px', fontWeight: 700, color: color ?? 'var(--text-primary)' }}>{value}</div>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{label}</div>
    </div>
  )
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const TH: CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '13px',
  whiteSpace: 'nowrap',
}

const TD: CSSProperties = {
  padding: '8px 12px',
  fontSize: '13px',
}

// ── Export status color map for use in other pages ───────────────────────────

export { STATUS_COLOR, STATUS_COLOR_GREEN, STATUS_ABBR, getStatusColors, statusLabelKey }
